import { FunctionComponent, useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Collapse, Tooltip } from '@mui/material';
import { ClientQuest } from 'src/types/client';
import { QuestStatus, getPriorityLabel } from 'src/enums/quests';
import {
    TitleCellContent,
    TickerButton,
    TickerArrowRight,
    TickerArrowDown,
    QuestTitleLabel,
    BellWrapper,
    BellDataTableCell,
    TasksExpandCell,
    TaskListBox,
    TaskRow,
    TaskTitle,
    TaskStatusBadge,
    DataTableRow,
    DataTableCell,
    CellContent,
    CellLabel,
    StyledBellIcon
} from './styled';
import { getTaskStatusColors } from 'src/types/task';
import {
    dateAndTimeSeperatedString,
    netTimeString
} from 'src/utils/dateFormat';
import { RoutesEnum } from 'src/types/navigation';
import { updateQuest } from 'src/api/quests';
import useAppDispatch from 'src/hooks/useAppDispatch';
import { fetchProjectQuests } from 'src/redux/client/service';
import { ClientSelectors } from 'src/redux/client/selectors';
import { useConversations } from 'src/redux/chat/selectors';
import { openChat } from 'src/redux/chat/slice';
import { fetchUserConversationState } from 'src/redux/chat/service';
import { CONVERSATION_PAGE_SIZE } from 'src/constants/chat';
import { isAdmin } from 'src/redux/auth/service';
import { getTaskStatusLabel } from 'src/types/task';
import QuestStatusCell from './QuestStatusCell';
import { BeehiveHumanReviewBadge } from 'src/shared';

export interface Props {
    clientQuest: ClientQuest;
}

const QuestTableRow: FunctionComponent<Props> = ({ clientQuest }: Props) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const {
        questsRowsPerPage,
        questsSearchQuery,
        questsStatusFilter,
        questsCurrentPage
    } = ClientSelectors();
    const conversations = useConversations();
    const admin = isAdmin();
    const [isExpanded, setIsExpanded] = useState(false);

    const { date, time } = useMemo(() => {
        return dateAndTimeSeperatedString(clientQuest.quest.created);
    }, [clientQuest.quest.created]);

    const questEntityId = useMemo(
        () => `q-${clientQuest.quest.id}`,
        [clientQuest.quest.id]
    );

    const conversation = useMemo(() => {
        return Object.values(conversations).find(
            (conv) => conv.entityId === questEntityId
        );
    }, [conversations, questEntityId]);

    const hasUnreadNotifications = useMemo(() => {
        return (
            conversation !== undefined && conversation.unreadMessageCount > 0
        );
    }, [conversation]);

    const currentIterationTasks = useMemo(() => {
        const tasks = clientQuest.quest.tasks || [];
        return tasks
            .filter((task) => task.iteration === clientQuest.quest.iterations)
            .sort((a, b) => a.created.localeCompare(b.created));
    }, [clientQuest.quest.tasks, clientQuest.quest.iterations]);

    const handleOpenConversation = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation();
            const projectId = clientQuest.quest.projectId;
            if (conversation?.conversationId) {
                if (conversation.entityId) {
                    try {
                        await dispatch(
                            fetchUserConversationState({
                                entityId: conversation.entityId,
                                resultsPerPage: CONVERSATION_PAGE_SIZE
                            })
                        )
                            .unwrap()
                            .then(() => {
                                dispatch(
                                    openChat({
                                        conversationId:
                                            conversation.conversationId,
                                        entityId: conversation.entityId,
                                        projectId:
                                            conversation.projectId ?? projectId
                                    })
                                );
                            });
                    } catch (error) {
                        // Ignore errors - ConversationContent will handle fetching
                    }
                }
            }
        },
        [dispatch, clientQuest.quest.projectId, conversation]
    );

    const handleTitleClick = useCallback(
        (e: React.MouseEvent) => {
            if (clientQuest.quest.status === QuestStatus.DRAFT) {
                const url = `${RoutesEnum.PLAN_QUEST}?questId=${clientQuest.quest.id}`;
                if (e.shiftKey) {
                    window.open(url, '_blank');
                } else {
                    navigate(url);
                }
            } else {
                const url = RoutesEnum.QUEST_DETAIL.replace(
                    ':id',
                    clientQuest.quest.id
                );
                if (e.shiftKey) {
                    window.open(url, '_blank');
                } else {
                    navigate(url);
                }
            }
        },
        [navigate, clientQuest.quest.id, clientQuest.quest.status]
    );

    const handleStatusChange = useCallback(
        async (newStatus: QuestStatus) => {
            try {
                await updateQuest(clientQuest.quest.id, { status: newStatus });
                dispatch(
                    fetchProjectQuests({
                        page: questsCurrentPage,
                        resultsPerPage: questsRowsPerPage,
                        q: questsSearchQuery,
                        statusFilters: questsStatusFilter
                    })
                );
            } catch (error) {
                console.error('Failed to update quest status:', error);
            }
        },
        [
            clientQuest.quest.id,
            dispatch,
            questsCurrentPage,
            questsRowsPerPage,
            questsSearchQuery,
            questsStatusFilter
        ]
    );

    const handleRefresh = useCallback(() => {
        dispatch(
            fetchProjectQuests({
                page: questsCurrentPage,
                resultsPerPage: questsRowsPerPage,
                q: questsSearchQuery,
                statusFilters: questsStatusFilter
            })
        );
    }, [
        dispatch,
        questsCurrentPage,
        questsRowsPerPage,
        questsSearchQuery,
        questsStatusFilter
    ]);

    const handleToggleExpand = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setIsExpanded((prev) => !prev);
    }, []);

    const handleTaskClick = useCallback(
        (taskId: string) => {
            navigate(RoutesEnum.TASK_DETAIL.replace(':id', taskId));
        },
        [navigate]
    );

    return (
        <>
            <DataTableRow>
                <DataTableCell>
                    <TitleCellContent>
                        {admin && (
                            <TickerButton onClick={handleToggleExpand}>
                                {isExpanded ? (
                                    <TickerArrowDown />
                                ) : (
                                    <TickerArrowRight />
                                )}
                            </TickerButton>
                        )}
                        <Tooltip
                            title={
                                clientQuest.quest.title.length > 50
                                    ? clientQuest.quest.title
                                    : ''
                            }
                            placement="top"
                            arrow
                        >
                            <QuestTitleLabel
                                onClick={(e) => handleTitleClick(e)}
                            >
                                {clientQuest.quest.title.length > 50
                                    ? `${clientQuest.quest.title.slice(0, 50)}...`
                                    : clientQuest.quest.title}
                            </QuestTitleLabel>
                        </Tooltip>
                    </TitleCellContent>
                </DataTableCell>

                <DataTableCell>
                    <CellContent>
                        <CellLabel>
                            {date} {time}
                        </CellLabel>
                    </CellContent>
                </DataTableCell>

                <DataTableCell>
                    <CellContent>
                        <CellLabel>
                            {admin
                                ? clientQuest.quest.priority
                                : getPriorityLabel(clientQuest.quest.priority)}
                        </CellLabel>
                    </CellContent>
                </DataTableCell>

                <DataTableCell>
                    <CellContent>
                        <CellLabel>
                            {netTimeString(clientQuest.netTime)}
                        </CellLabel>
                    </CellContent>
                </DataTableCell>

                <DataTableCell>
                    <CellContent>
                        <QuestStatusCell
                            clientQuest={clientQuest}
                            onStatusChange={handleStatusChange}
                            onRefresh={handleRefresh}
                        />
                    </CellContent>
                </DataTableCell>
                <DataTableCell>
                    <CellContent>
                        <BeehiveHumanReviewBadge
                            status={
                                clientQuest.beehiveHumanReviewStatus ??
                                clientQuest.quest.beehiveHumanReviewStatus
                            }
                        />
                    </CellContent>
                </DataTableCell>
                <BellDataTableCell>
                    {hasUnreadNotifications && (
                        <Tooltip
                            title="Unread notifications - Click to open conversation"
                            arrow
                            placement="top"
                        >
                            <BellWrapper
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    handleOpenConversation(e);
                                }}
                            >
                                <StyledBellIcon />
                            </BellWrapper>
                        </Tooltip>
                    )}
                </BellDataTableCell>
            </DataTableRow>

            {admin && (
                <DataTableRow>
                    <TasksExpandCell colSpan={6}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <TaskListBox>
                                {currentIterationTasks.map((task) => {
                                    const { bg, textColor: badgeColor } =
                                        getTaskStatusColors(task.status);
                                    return (
                                        <TaskRow
                                            key={task.id}
                                            onClick={() =>
                                                handleTaskClick(task.id)
                                            }
                                        >
                                            <TaskTitle>{task.title}</TaskTitle>
                                            <TaskStatusBadge
                                                badgebg={bg}
                                                badgecolor={badgeColor}
                                            >
                                                {getTaskStatusLabel(
                                                    task.status
                                                )}
                                            </TaskStatusBadge>
                                        </TaskRow>
                                    );
                                })}
                            </TaskListBox>
                        </Collapse>
                    </TasksExpandCell>
                </DataTableRow>
            )}
        </>
    );
};

export default QuestTableRow;
